import { Image } from '@chakra-ui/react';

import ImpactReporth2 from '../../../../../components/sections/impactreport/impact-h2';
import ImpactReporth3 from '../../../../../components/sections/impactreport/impact-h3';

import ghgTotalImg from  '../../../../../components/ui/corporateresponsibility/impact/2024-impact-report/appendices/appendix-b/total_emissions_annual.png';

export default function TwentyTwentyFourImpactReportAppendixB() {
    return(
        <section id='appendix-b-section'>
            <div className='container flow'>
                <ImpactReporth2>Appendix B: Greenhouse Gas Emissions Methodology</ImpactReporth2>
                <ImpactReporth3>Statement on revisions to 2022 GHG Emissions Disclosure</ImpactReporth3>
                <p>
                    Columbia Sportswear Company has recalculated the Company's prior 2022 corporate emissions inventory to reflect the most recent global warming potentials (AR6) published by the IPCC. An update to AR6 GWPs resulted in a 3% increase in the relative warming effects of N<sub>2</sub>O and a 19% increase in the relative warming effects of refrigerant R410A from the Company's prior emissions inventories. Other changes include an update to the Company's methodologies for extrapolating energy usage to reflect industry best practices and standards.
                </p>
                <figure className='table-img'>
                    <figcaption>
                        <span style={{ textTransform: 'uppercase' }}>Total Emissions &mdash; Annual (2022 & 2024)</span>
                        <br />
                        <span style={{ textTransform: 'uppercase' }}>GHG Emissions</span> (metric tons CO2e)
                    </figcaption>
                    <Image src={ghgTotalImg} alt='Greenhouse gas total emissions chart' />
                    <footer style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                        <p><sup>*</sup>Revised numbers, based on updated calculation factors</p>
                    </footer>
                </figure>

                <ImpactReporth3>Scope 1 & 2 GHG Emissions Methodology</ImpactReporth3>
                <h4>Standards</h4>
                <p>
                    This report has been prepared in accordance with the relevant Greenhouse Gas (GHG) Protocol Standards, including:
                </p>
                <ul>
                    <li>
                        GHG Protocol Corporate Accounting and Reporting Standard (WBCSD & WRI, 2004)
                    </li>
                    <li>
                        GHG Protocol Scope 2 Guidance (WBCSD & WRI, 2015) 
                    </li>
                    <li>
                        GHG Protocol Corporate Value Chain (Scope 3) Accounting and Reporting Standard (WBCSD & WRI, 2011)
                    </li>
                </ul>

                <h4>Timeframe of Reporting</h4>
                <p>
                    CSC reports emissions on a calendar year basis in line with its financial reporting year. GHG emissions in this report reflect business activities taking place January 1 - December 31 for the years 2022 and 2024.
                </p>

                <h4>Organizational Boundary</h4>
                <p>
                    In accordance with the GHG Protocol, CSC includes emissions from operations over which the Company has operational control (i.e., CSC is responsible for accounting for GHG emissions from its locations (whether leased or owned) for which it has direct control over operations). In retail locations, CSC assumes operational control at sites where CSC (1) operates the lease, (2) owns all inventory, and (3) controls pricing. The footprint represents all owned operations or operations for which CSC has operational control, even when not owned by CSC.
                </p>

                <h4>Facilities</h4>
                <div className='table-wrapper' tabIndex="0" role="region" aria-labelledby="tableCaption_03">
                    <table className='reduced-font-table' style={{ tableLayout: 'auto', width: '100%'}}>
                        <thead>
                            <tr>
                                <th scope="col">Facility Type</th>
                                <th scope="col">Scope Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope='row'>Retail</th>
                                <td>Includes CSC owned or operated stores globally.</td>
                            </tr>
                            <tr>
                                <th scope='row'>Distribution Centers</th>
                                <td>Includes CSC owned or operated DCs globally that are not contract-based or third-party operated.</td>
                            </tr>
                            <tr>
                                <th scope='row'>Storage Facilities</th>
                                <td>Includes on-site and off-site storage locations globally that are not third-party operated.</td>
                            </tr>
                            <tr>
                                <th scope='row'>Offices</th>
                                <td>Includes CSC owned or operated offices globally.</td>
                            </tr>
                             <tr>
                                <th scope='row'>Corporate Housing</th>
                                <td>CSC leases a small number of corporate housing locations internationally to assist employees during temporary relocations.</td>
                            </tr>
                            <tr>
                                <th scope='row'>Data Centers</th>
                                <td>Data centers are operated on a contractual basis and are therefore not included in CSC’s Scope 1 and 2 footprint.</td>
                            </tr>
                            <tr>
                                <th scope='row'>Temporary Retail Locations (“Pop-Ups”)</th>
                                <td>Pop-Up Agreements (located in China, Japan, and South Korea) operate for days or weeks and cover a limited area within any retail shop (e.g., one aisle within a department store). Due to the immaterial lease duration and square footage, Pop-Ups are not included in CSC’s Scope 1 and 2 footprint.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4>Emissions Factors</h4>
                <p>
                    This table outlines the emission factor sources used in FY22 and FY24 emissions calculations.
                </p>
                <div className='table-wrapper' tabIndex="0" role="region" aria-labelledby="tableCaption_03">
                    <table className='reduced-font-table'>
                        <thead>
                            <tr>
                            <th scope="col">Emissions Source</th>
                            <th scope="col" style={{ width: '15rem' }}>Emission Source Type</th>
                            <th scope="col">Emission Factor Employed</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Natural Gas</td>
                            <td>EPA Greenhouse Gas Emission Factors Hub, Table 1 (Stationary Combustion)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Diesel</td>
                            <td>EPA Greenhouse Gas Emission Factors Hub, Table 8 (Passenger Car, Light-duty Truck, Medium- and Heavy-Duty Truck)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Refrigerant</td>
                            <td>Custom emissions factors based on actual refrigerant leak rates from the Company’s HVAC units.</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Propane</td>
                            <td>EPA Greenhouse Gas Emission Factors Hub, Table 1 (Stationary Combustion)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Gasoline</td>
                            <td>EPA Greenhouse Gas Emission Factors Hub, Table 8 (Passenger Car, Light-duty Truck, Medium- and Heavy-Duty Truck)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity (US) – location-based</td>
                            <td>EPA Emissions &amp; Generation Resource Integrated Database (eGRID)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity (US) – market-based</td>
                            <td>Green-e® Residual Mix (CO2 only), eGRID (CH4 and N2O)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity (Canada) – location- &amp; market-based</td>
                            <td>Canada’s National Inventory Report (NIR)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity (Europe) – location-based</td>
                            <td>International Energy Agency (IEA) World Electricity Emissions Factors</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity (Europe) – market-based</td>
                            <td>Association of Issuing Bodies (AIB) European Residual Mix (CO2 only), IEA (CH4 and N2O)</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity (all other locations) – location- &amp; market-based</td>
                            <td>International Energy Agency (IEA) World Electricity Emissions Factors</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4>Activity Data</h4>
                <p>
                    CSC aims to collect as much high-quality, primary (actual) data across its activities as possible. However, primary data may not be available for some activities or may not be of sufficient quality. In those cases, secondary data (estimation, gap fill, or extrapolation) may be used. The chart below identifies whether the calculations were based on primary or secondary data.
                </p>
                <div className='table-wrapper' tabIndex="0" role="region" aria-labelledby="tableCaption_04">
                    <table className='reduced-font-table' style={{ tableLayout: 'auto', width: '100%'}}>
                        <thead>
                            <tr>
                            <th scope="col">Emissions Source</th>
                            <th scope="col" style={{ width: '12rem' }}>Activity Data</th>
                            <th scope="col" style={{ width: '10rem' }}>Data type (actual vs. gap-fill)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Refrigerant Leakage</td>
                            <td>Primary &amp; Secondary</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Natural Gas</td>
                            <td>Primary &amp; Secondary</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Diesel</td>
                            <td>Primary</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Propane</td>
                            <td>Primary</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 1</th>
                            <td>Gasoline</td>
                            <td>Primary</td>
                            </tr>
                            <tr>
                            <th scope='row'>Scope 2</th>
                            <td>Electricity</td>
                            <td>Primary &amp; Secondary</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4>Global Warming Potential Values</h4>
                <p>
                    The Greenhouse Gas Emissions inventory in this report was calculated using Global Warming Potential (GWP) values aligned with the Sixth Annual Report as published by the Intergovernmental Panel on Climate Change (IPCC).
                </p>

                <h4>Renewable Energy Certificates</h4>
                <p>
                    In 2024, CSC purchased 5,882 MWh of renewable energy certificates (RECs).<span className='fn-link' id="fn7"><a href="#fn1_content"><sup>7</sup></a></span>
                </p>

                <h4>Management’s Significant Assumptions</h4>
                <p>
                    Natural gas applies gap filling methodology for all sites and all regions, unless explicitly communicated that a site does not combust natural gas.
                </p>
                <p>
                    Electricity applies gap filling methodology for all sites and regions.
                </p>
                <p>
                    Fugitive emissions (refrigerant leakage) applies gap filling methodology for all sites and regions.
                </p>
                <p>
                    If a facility reports energy consumption data for only a portion of its operating period (e.g., a retail location that only reports energy usage for 6 of its 12-month operating lease), missing data are gap-filled (i.e., extrapolated) using daily annual average from the reported energy consumption data.
                </p>
                <p>
                    If a US facility does not report any energy usage during the year, Scope 1 and 2 activity data (e.g., natural gas and/or electricity) are estimated using energy use intensities (EUIs) provided by the Commercial Buildings Energy Consumption Survey (CBECS<span className='fn-link' id="fn8"><a href="#fn8_content"><sup>8</sup></a></span>) via US census region or division.
                </p>
                <p>
                    If a non-US facility does not report any energy usage during the year, Scope 1 and 2 activity data are estimated using climate zones defined by the American Society of Heating, Refrigerating and Air-Conditioning Engineers (ASHRAE<span className='fn-link' id="fn9"><a href="#fn9_content"><sup>9</sup></a></span>) via latitude-longitude coordinates.
                </p>
                <p>
                    The below chart outlines Management’s Significant Assumptions both for the primary data used, and for the gap fill performed, where primary data was not available (i.e., secondary data) in this Scope 1 and 2 footprint.
                </p>
                <div className='table-wrapper' tabIndex="0" role="region" aria-labelledby="tableCaption_05">
                    <table className='reduced-font-table'>
                        <thead>
                            <tr>
                            <th scope="col">Emission Source</th>
                            <th scope="col">Management’s Assumptions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                            <th scope='row'>Natural Gas</th>
                            <td>
                                <strong>Primary:</strong>
                                <ul>
                                <li>For storage facilities that are associated with retail stores and do not have lease dates available, the lease term from the retail site is associated with the storage location.</li>
                                </ul>
                                <strong>Gap-fill:</strong>
                                <ul>
                                <li>Previously assigned annual EUIs for each facility are provided in kWh/sqft. Total natural gas energy use (in kWh) is calculated by multiplying the annual EUI by total square footage.</li>
                                </ul>
                                <p>Country-specific natural gas consumption patterns from the IEA are for the following regional assumptions are utilized in calculations:</p>
                                <ul>
                                <li>
                                    In Korea, 43% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.<span className='fn-link' id="fn10"><a href="#fn10_content"><sup>10</sup></a></span> In 2022, the residential and commercial sectors accounted for 26% of total natural gas end use consumption.<span className='fn-link' id="fn11"><a href="#fn11_content"><sup>11</sup></a></span> As a common fuel for indoor heating, we assume all Korea facilities combust natural gas. In the absence of real activity data and unless otherwise noted on an individual basis, natural gas use in Korea is extrapolated for all CSC locations.
                                </li>
                                <li>
                                    In Japan, 34% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.<span className='fn-link' id="fn12"><a href="#fn12_content"><sup>12</sup></a></span> In 2022, the residential and commercial sectors accounted for 20% of total natural gas end use consumption.<span className='fn-link' id="fn13"><a href="#fn13_content"><sup>13</sup></a></span> As a common fuel for indoor heating, we assume all Japan facilities consume natural gas. In the absence of real activity data and unless otherwise noted on an individual basis, natural gas use in Japan is extrapolated for all CSC locations.
                                </li>
                                <li>
                                    In China, 75% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.<span className='fn-link' id="fn14"><a href="#fn14_content"><sup>14</sup></a></span> In 2023, after industrial uses, the residential and commercial sector was the largest consumer of natural gas in China.<span className='fn-link' id="fn15"><a href="#fn15_content"><sup>15</sup></a></span> As a common fuel across China, we assume all China facilities consume natural gas. In the absence of real activity data and unless otherwise noted on an individual basis, natural gas use in China is extrapolated for all CSC locations.
                                </li>
                                <li>
                                    In Europe, 62% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.<span className='fn-link' id="fn16"><a href="#fn16_content"><sup>16</sup></a></span> In 2022, the residential and commercial sector used more natural gas than the electricity generating sector (7.4MJ vs 6.5MJ, respectively). As a common fuel across Europe, we assume all Europe facilities consume natural gas. In the absence of real activity data and unless otherwise noted on an individual basis, natural gas use in Europe is extrapolated for all CSC locations.
                                </li>
                                <li>
                                    In Canada, 43% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.<span className='fn-link' id="fn17"><a href="#fn17_content"><sup>17</sup></a></span> In 2022, the residential and commercial sectors accounted for roughly 23% of total natural gas end use consumption. As a common fuel for indoor heating, we assume all Canada facilities consume natural gas. In the absence of real activity data and unless otherwise noted on an individual basis, natural gas use in Canada is extrapolated for all CSC locations.
                                </li>
                                </ul>
                            </td>
                            </tr>
                            <tr>
                            <th scope='row'>Propane</th>
                            <td>
                                <strong>Primary and Secondary:</strong>
                                <ul>
                                <li>Only one site in CSC’s facility list reports propane usage. As a result, it is assumed that propane usage is not an average day-to-day practice, and therefore a gap-filling exercise was not performed on other global facilities.</li>
                                </ul>
                            </td>
                            </tr>
                            <tr>
                            <th scope='row'>Mobile Combustion (North America)</th>
                            <td>
                                <strong>Primary:</strong>
                                <ul>
                                <li>Actual milage for each vehicle was not tracked for 2022 or 2024.</li>
                                </ul>
                                <strong>Secondary:</strong>
                                <ul>
                                <li>CSC, with the aid of Facility Managers, reviewed the list of vehicles and determined a reasonable annual mileage for each vehicle, based on primary use and type of the vehicle.</li>
                                </ul>
                            </td>
                            </tr>
                            <tr>
                            <th scope='row'>Mobile Combustion (Europe)</th>
                            <td>
                                <strong>Primary:</strong>
                                <ul>
                                <li>Actual mileage for each vehicle was not tracked for 2022 or 2024.</li>
                                </ul>
                                <strong>Secondary:</strong>
                                <ul>
                                <li>CSC identified the maximum of kilometers (Km) per the vehicle’s lease agreement and divided it by the lease term to obtain an annual distance.</li>
                                </ul>
                            </td>
                            </tr>
                            <tr>
                            <th scope='row'>Equipment</th>
                            <td>
                                <strong>Primary:</strong>
                                <ul>
                                <li>CSC’s Distribution Centers (DC) are the only facility type that use equipment during day-to-day operations. Data provided by DC managers are only applicable to those specific DCs, and are not extrapolated to the rest of CSC’s Scope 1 & 2 footprint.</li>
                                </ul>
                            </td>
                            </tr>
                            <tr>
                            <th scope='row'>Fugitive Emissions (Refrigerant Leakage)</th>
                            <td>
                                <strong>Primary:</strong>
                                <ul>
                                <li>Refrigerant leakage (lbs) was not tracked for 2022 or 2024.</li>
                                <li>A list of over 500 HVAC units across North America was provided. Charge capacities of these units were obtained through individual user manuals.</li>
                                </ul>
                                <strong>Secondary:</strong>
                                <ul>
                                <li>Where charge capacity was not listed for a unit, the average charge capacity of all listed units (~16.9lbs refrigerant) was used.</li>
                                <li>Unless otherwise determinable, HVAC units and AC systems were assumed to be "Residential and Commercial A/C including Heat Pumps" per EPA classification<span className='fn-link' id="fn18"><a href="#fn18_content"><sup>18</sup></a></span>.</li>
                                <li>Where Refrigerant/ASHRAE # was not listed, the assumed refrigerant was R410A (the replacement for R22 mandated under the U.S. Clean Air Act and the Montreal Protocol on Substances that Deplete the Ozone Layer).</li>
                                <li>Only GHGs listed in the Kyoto protocol should be included in CSC’s Scope 1 &amp; 2 inventory. HCFC-22 (R-22) is not a listed gas in the Kyoto Protocol, and as such, is excluded.</li>
                                </ul>
                            </td>
                            </tr>
                            <tr>
                            <th scope='row'>Electricity</th>
                            <td>
                                <strong>Primary:</strong>
                                <ul>
                                <li>For storage facilities that are associated with retail stores and do not have lease dates available, the lease term from the retail site is associated with the storage location.</li>
                                </ul>
                                <strong>Gap-fill:</strong>
                                <ul>
                                <li>Previously assigned annual EUIs for each facility are provided in kWh/sqft. Total natural gas energy use (in kWh) is calculated by multiplying the annual EUI by total square footage.</li>
                                </ul>
                            </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h4>Review</h4>
                <p>
                    In 2024, the Company underwent pre-assurance of 2024 scope 1 and 2 data by an outside third party. The calculation methodology, including the scientific basis for underlying assumptions and emissions factors leveraged within the calculations, were reviewed by an independent auditor for alignment with GHG Protocol standards and guidelines.
                </p>

                <h4>Uncertainty</h4>
                <p>
                    GHG emissions quantification is subject to significant inherent measurement uncertainty because of such things as GHG emissions factors that are used in mathematical models to calculate GHG emissions, and the inability of these models, due to incomplete scientific knowledge and other factors, to accurately measure under all circumstances the relationship between various inputs and the resultant GHG emissions. Environmental and energy use data used in GHG emissions calculations are subject to inherent limitations, given the nature and the methods used for measuring such data. The selection by management of different but acceptable measurement techniques could have resulted in materially different amounts or metrics being reported.
                </p>
                <p>
                    The preparation of the energy consumption metrics requires management to establish the criteria, make determinations as to the relevancy of information to be included, and make assumptions that affect reported information. The selection by management of different but acceptable measurement techniques could have resulted in materially different amounts or metrics being reported.
                </p>
            </div>
        </section>
    );
}